// One row per bucket, sorted by conversion rate.
//
// Two numbers per row and nothing else: how big the bucket is, and how much
// better or worse it converts. That pair is the whole decision, because a lift
// is only worth acting on when enough leads sit behind it, and everything else
// the row used to carry was detail about a bucket you had already decided to
// look at. Share of conversions went with the rest: it is population share
// times lift, so drawing it spent a third encoding on a fact the other two
// already contain.
//
// The lift bar doubles every step and is centred on the node's rate. On a linear
// ratio scale the entire under-performing range is squeezed into a quarter of
// the width, which is why weak splits were invisible.
//
// Rows whose difference from the rest could be chance are greyed. That is not
// the same as greying small rows: the largest bucket on a real attribute, 1,554
// leads, is one of the ones that goes quiet, while a 42 lead bucket at 41
// conversions is among the most decisive rows there is.
//
// Detail is on hover, in a popover anchored to the bar.
//
// This took four attempts and the history is worth keeping, because three of the
// four were wrong in the same way. The overlays appeared to do nothing, and the
// real cause was that the dev server never reloads the browser: the client config
// has a bare HotModuleReplacementPlugin, no module accepts an update, and without
// `reload=true` an open tab keeps the bundle it first downloaded through every
// recompile. Fixes were judged against code that was not running.
//
// One of the four was a genuine bug and is the reason this file spreads `injected`
// below: an overlay component that names only its own props silently discards the
// ref and the positioning style OverlayTrigger clones into it, and the popover then
// mounts unpositioned. A native title was tried as a mechanism that cannot fail,
// and it does not fail, but the browser draws it at the corner of the viewport
// rather than at the bar, which is its own kind of not appearing.
import React from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
  AttributeStats,
  BucketStat,
  LadderItem,
  ladderItems,
  RolledUpRows,
} from './bucketStats';
import './nodeInsights.css';

const count = (n: number) => n.toLocaleString('en-US');
const pct = (n: number, digits = 1) => `${(n * 100).toFixed(digits)}%`;
const formatP = (p: number) => (p < 0.001 ? 'under 0.001' : p.toFixed(3));

/** Clamped so one runaway bucket cannot flatten every other bar. */
const LIFT_STEPS = 2;

function liftGeometry(lift: number): { left: number; width: number } {
  const steps =
    lift > 0
      ? Math.max(-LIFT_STEPS, Math.min(LIFT_STEPS, Math.log2(lift)))
      : -LIFT_STEPS;
  const width = (Math.abs(steps) / LIFT_STEPS) * 50;
  return { left: steps >= 0 ? 50 : 50 - width, width: Math.max(0.7, width) };
}

function toneFor(row: BucketStat): string {
  const up = row.lift >= 1;
  if (row.clears) return up ? '#1e77cc' : '#d11f4d';
  return up ? '#a8caea' : '#eda9bb';
}

/**
 * Everything `OverlayTrigger` clones into its overlay: the ref and the style
 * Popper positions it with, plus placement and arrow geometry. An overlay that
 * names only its own props drops all of it and mounts unpositioned.
 */
type Injected = Record<string, unknown>;

/**
 * Size, and whether the row is big enough for its lift to mean anything.
 *
 * `forwardRef`, and that is the whole reason these tooltips render at all.
 * `OverlayTrigger` calls the overlay as a FUNCTION, so the props it hands over
 * include a `ref`; spreading that into a plain function component makes React
 * drop it with "Function components cannot be given refs", Popper is left with
 * no element to measure, and nothing is ever positioned. The cursor still turns
 * to the help pointer, because the trigger side was never broken, which is why
 * this read as a dead hover rather than as a missing ref.
 */
const ShareTip = React.forwardRef<
  HTMLDivElement,
  { row: BucketStat; stats: AttributeStats } & Injected
>(function ShareTip({ row, stats, ...injected }, ref) {
  return (
    <Popover ref={ref} {...injected} id={`nins-share-${row.rawName}`}>
      <Popover.Header as="h3">{row.name}</Popover.Header>
      <Popover.Body>
        <div className="nins-tip-row">
          <span>Share of leads</span>
          <span>
            {pct(row.populationShare)}, {count(row.population)} of{' '}
            {count(stats.node.population)}
          </span>
        </div>
        <div className="nins-tip-row">
          <span>Conversions</span>
          <span>
            {count(row.conversions)}, converting at {pct(row.rate, 2)}
          </span>
        </div>
      </Popover.Body>
    </Popover>
  );
});

const LiftTip = React.forwardRef<
  HTMLDivElement,
  { row: BucketStat; stats: AttributeStats } & Injected
>(function LiftTip({ row, stats, ...injected }, ref) {
  const ceiling = stats.baseline ? 1 / stats.baseline : 0;
  return (
    <Popover ref={ref} {...injected} id={`nins-lift-${row.rawName}`}>
      <Popover.Header as="h3">{row.name}</Popover.Header>
      <Popover.Body>
        <div className="nins-tip-row">
          <span>Lift against the node</span>
          <span>{row.lift.toFixed(2)}x</span>
        </div>
        <div className="nins-tip-row">
          <span>Lift against the rest of it</span>
          <span>{row.liftVsRest.toFixed(2)}x</span>
        </div>
        <div className="nins-tip-note">
          The bar doubles each step, so 0.5x and 2x sit the same distance either
          side of the centre. Nothing here can pass {ceiling.toFixed(1)}x,
          because that is a 100% conversion rate on a {pct(stats.baseline, 2)}{' '}
          baseline.
        </div>
      </Popover.Body>
    </Popover>
  );
});

function Row({
  row,
  stats,
  state,
}: {
  row: BucketStat;
  stats: AttributeStats;
  /** 'lit' and 'dimmed' only while the proposed split is being hovered. */
  state: 'none' | 'lit' | 'dimmed';
}) {
  const tone = toneFor(row);
  const { left, width } = liftGeometry(row.lift);

  return (
    <tr
      className={`${row.clears ? '' : 'nins-row--quiet'} ${
        state === 'lit' ? 'nins-row--lit' : ''
      } ${state === 'dimmed' ? 'nins-row--dimmed' : ''}`}
    >
      <td className="nins-name" title={row.name}>
        {row.name}
      </td>
      <td>
        <OverlayTrigger
          placement="top"
          delay={{ show: 120, hide: 0 }}
          // Mounted on the body, not in place. The ladder sits inside a panel
          // that scrolls, and an ancestor with `overflow: hidden` clips a
          // popover rendered beneath it: the cursor still turns to the help
          // pointer, because the trigger itself is fine, and nothing appears.
          // Undefined on the server, where there is no document and the default
          // is the right answer.
          container={typeof window === 'undefined' ? undefined : document.body}
          // Render-function form, not an element: it is what carries the ref
          // through the spread into Popover, which a cloned element silently
          // loses on a component that does not forward it.
          overlay={(injected) => (
            <ShareTip {...injected} row={row} stats={stats} />
          )}
        >
          <div className="nins-share">
            <span className="nins-bar">
              <i style={{ width: `${row.populationShare * 100}%` }} />
            </span>
          </div>
        </OverlayTrigger>
      </td>
      <td>
        <OverlayTrigger
          placement="top"
          delay={{ show: 120, hide: 0 }}
          container={typeof window === 'undefined' ? undefined : document.body}
          overlay={(injected) => (
            <LiftTip {...injected} row={row} stats={stats} />
          )}
        >
          <div className="nins-track">
            <span
              className="nins-fill"
              style={{ left: `${left}%`, width: `${width}%`, background: tone }}
            />
          </div>
        </OverlayTrigger>
      </td>
      <td
        className={`nins-lift ${
          row.lift >= 1 ? 'nins-lift--up' : 'nins-lift--down'
        }`}
      >
        {row.lift.toFixed(2)}x
        {!row.clears && (
          <span
            className="nins-flag"
            title={`Not distinguishable from the rest of this node (p = ${formatP(
              row.p
            )})`}
          >
            &#9888;
          </span>
        )}
      </td>
    </tr>
  );
}

function Rollup({
  rollup,
  shape,
  onExpand,
}: {
  rollup: RolledUpRows;
  shape: AttributeStats['shape'];
  onExpand: () => void;
}) {
  return (
    <tr className="nins-rollup">
      <td colSpan={4}>
        <button type="button" className="nins-more" onClick={onExpand}>
          show all
        </button>
        {rollup.count} more{' '}
        {shape === 'multi'
          ? 'values'
          : shape === 'ordinal'
          ? 'levels'
          : 'buckets'}{' '}
        between {pct(rollup.lowestRate, 2)} and {pct(rollup.highestRate, 2)},
        the largest covering {pct(rollup.largestShare)} of the node. None is a
        legal split, and none differs measurably from the rest.
      </td>
    </tr>
  );
}

export default function BucketLadder({
  stats,
  expanded,
  onExpand,
  highlight,
}: {
  stats: AttributeStats;
  expanded: boolean;
  onExpand: () => void;
  /**
   * Rows on the matched side of the split being hovered above. A rule like
   * "Prospect or better" does not say which buckets that is, so hovering the
   * proposal lights them and quiets everything else.
   */
  highlight: number[] | null;
}): JSX.Element {
  const items: LadderItem[] = ladderItems(stats, expanded);
  const lit = highlight ? new Set(highlight.map((i) => stats.rows[i])) : null;
  const ticks: [string, number][] = [
    ['0.25x', 0],
    ['0.5x', 25],
    ['1x', 50],
    ['2x', 75],
    ['4x', 100],
  ];

  return (
    <table className="nins-table">
      <thead>
        <tr>
          <th>
            {stats.shape === 'multi'
              ? 'Value'
              : stats.shape === 'ordinal'
              ? 'Level'
              : 'Bucket'}
          </th>
          <th>Share of leads</th>
          <th>Lift</th>
          <th aria-label="Lift value" />
        </tr>
      </thead>
      <tbody>
        {items.map((item, i) =>
          item.kind === 'rollup' ? (
            // eslint-disable-next-line react/no-array-index-key
            <Rollup
              key={`rollup-${i}`}
              rollup={item.rollup}
              shape={stats.shape}
              onExpand={onExpand}
            />
          ) : (
            <Row
              key={item.row.rawName}
              row={item.row}
              stats={stats}
              state={
                // eslint-disable-next-line no-nested-ternary
                !lit ? 'none' : lit.has(item.row) ? 'lit' : 'dimmed'
              }
            />
          )
        )}
        <tr>
          <td colSpan={2} />
          <td>
            <div className="nins-axis">
              {ticks.map(([label, at]) => (
                <span
                  key={label}
                  className={at === 50 ? 'nins-axis--base' : ''}
                  style={{ left: `${at}%` }}
                >
                  {label}
                </span>
              ))}
            </div>
          </td>
          <td />
        </tr>
      </tbody>
    </table>
  );
}
